import React, { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooterButtons,
  ModalHeader,
  Flex,
  Box,
  TextInput,
  Switch,
  Text,
  Button,
  Icon,
  ModalCloseButton,
  Alert,
  useToast,
  Center,
} from '@televet/kibble-ui';
import {
  useGetDirectBookingAppointmentTypesQuery,
  useGetDirectBookingProvidersQuery,
  useUpsertDirectBookingExclusionRuleMutation,
  AppointmentTypeWhereUniqueInput,
  ClinicEmployeeWhereUniqueInput,
  DirectBookingExclusionTimeSlotCreateManyExclusionRuleInput,
  DirectBookingExclusionTimeSlotUpsertWithWhereUniqueWithoutExclusionRuleInput,
  DirectBookingExclusionRuleCreateInput,
  DirectBookingExclusionRuleUpdateInput,
  ExclusionRuleSelectionFragment,
} from 'shared/types/graphql';
import ExclusionaryTimeSlot, { GroupedTimeRanges } from './ExclusionaryTimeSlot';
import { add } from 'date-fns';
import { getDateFromTimeString, getUTCTimeStringFromDate } from '../utils';
import LabelPopover from './LabelPopover';
import { getTimezoneAbbreviation } from 'shared/utils/timezoneDisplayNames';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import MultiSelectWithTags, { MultiSelectItem } from 'shared/components/MultiSelectWithTags';
import cuid from 'cuid';

export interface TimeRange {
  id: string;
  updatedAt: Date;
  dayOfWeek: number;
  startAt: string;
  endAt: string;
  isNew?: boolean;
  isDeleted?: boolean;
}

interface ExclusionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedExclusion: ExclusionRuleSelectionFragment | undefined;
  clinicId: string;
  clinicTimezone: string;
}

const ExclusionsModal = ({
  isOpen,
  onClose,
  selectedExclusion,
  clinicId,
  clinicTimezone,
}: ExclusionModalProps): JSX.Element => {
  const toast = useToast();
  // TODO: consider using forms provider such as react-hook-form for error management (and more) here
  const [, setErrorMap] = useState<Map<string, string | ReactElement>>(new Map()); // map range ID to error
  const [timeRangeError, setTimeRangeError] = useState<string | ReactElement>('');
  const [showTypesError, setShowTypesError] = useState(false);
  const [showProvidersError, setShowProvidersError] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [showTimeSlotsError, setShowTimeSlotsError] = useState(false);
  const [ruleName, setRuleName] = useState(selectedExclusion?.name || '');
  const [ruleEnabled, setRuleEnabled] = useState(selectedExclusion?.isActive || false);
  const [appointmentTypes, setAppointmentTypes] = useState<string[]>(
    selectedExclusion?.appointmentTypes.map((type) => type.id) || [],
  );
  const [providers, setProviders] = useState<string[]>(
    selectedExclusion?.providers.map((provider) => provider.id) || [],
  );

  // Initialize groupedTimeRanges from existing exclusion data
  const initializeGroupedTimeRanges = useCallback((): GroupedTimeRanges[] => {
    if (!selectedExclusion?.timeSlots.length) {
      return [];
    }

    const timeRanges: TimeRange[] = selectedExclusion.timeSlots.map((range) => ({
      id: range.id,
      updatedAt: range.updatedAt,
      dayOfWeek: range.dayOfWeek,
      startAt: range.startTime,
      endAt: range.endTime,
    }));

    const groupedByTime: Record<string, GroupedTimeRanges> = {};

    timeRanges.forEach((range) => {
      const key = `${range.startAt}-${range.endAt}`;

      if (!groupedByTime[key]) {
        groupedByTime[key] = {
          id: `group_${cuid()}`,
          startAt: range.startAt,
          endAt: range.endAt,
          daysOfWeek: [range.dayOfWeek],
          timeRanges: [range],
        };
      } else if (!groupedByTime[key].daysOfWeek.includes(range.dayOfWeek)) {
        groupedByTime[key].daysOfWeek.push(range.dayOfWeek);
        groupedByTime[key].timeRanges.push(range);
      }
    });

    return Object.values(groupedByTime);
  }, [selectedExclusion?.timeSlots]);

  const [groupedTimeRanges, setGroupedTimeRanges] = useState<GroupedTimeRanges[]>(initializeGroupedTimeRanges);

  // Generate grouped time ranges when modal opens
  useEffect(() => {
    setGroupedTimeRanges(initializeGroupedTimeRanges());
  }, [initializeGroupedTimeRanges, isOpen]);

  const { data: appointmentTypesData, loading: loadingTypes } = useGetDirectBookingAppointmentTypesQuery({
    variables: { clinicId },
    skip: !clinicId,
  });

  const { data: providersData, loading: loadingProviders } = useGetDirectBookingProvidersQuery({
    variables: { clinicId },
    skip: !clinicId,
  });

  const isLoading = useMemo(() => loadingTypes || loadingProviders, [loadingProviders, loadingTypes]);

  const [saveExclusion] = useUpsertDirectBookingExclusionRuleMutation({
    refetchQueries: ['getDirectBookingExclusions'],
  });

  const appointmentTypeItems: MultiSelectItem[] = useMemo(() => {
    return (
      appointmentTypesData?.findManyAppointmentType
        ?.filter((type) => !!type.name)
        .map((type) => ({
          id: type.id,
          label: type.name,
        })) || []
    );
  }, [appointmentTypesData?.findManyAppointmentType]);

  const providerItems: MultiSelectItem[] = useMemo(() => {
    return (
      providersData?.findManyClinicEmployee?.map((employee) => ({
        id: employee.id,
        label: `${employee.firstName} ${employee.lastName}`,
      })) || []
    );
  }, [providersData?.findManyClinicEmployee]);

  const handleNameChange = useCallback((name: string) => {
    setRuleName(name);
    if (!!name) {
      setShowNameError(false);
    }
  }, []);

  const handleAppointmentTypeSelected = useCallback((selectedIds: string[]) => {
    if (selectedIds.length) {
      setShowTypesError(false);
    }
    setAppointmentTypes(selectedIds);
  }, []);

  const handleProviderSelected = useCallback((selectedIds: string[]) => {
    if (selectedIds.length) {
      setShowProvidersError(false);
    }
    setProviders(selectedIds);
  }, []);

  const handleAddTimeSlot = useCallback(() => {
    setShowTimeSlotsError(false);
    setGroupedTimeRanges((currentGroups) => {
      // default start time is midnight clinic time
      const midnight = new Date().setHours(0, 0, 0);
      const startDate = new Date(midnight);
      const startAt = getUTCTimeStringFromDate(startDate);
      const endAt = getUTCTimeStringFromDate(add(startDate, { hours: 1 }));

      const newTimeRange: TimeRange = {
        id: `newRange_${cuid()}`,
        updatedAt: new Date(),
        dayOfWeek: 1,
        startAt,
        endAt,
        isNew: true,
      };

      const newGroup: GroupedTimeRanges = {
        id: `group_${cuid()}`,
        startAt,
        endAt,
        daysOfWeek: [1],
        timeRanges: [newTimeRange],
      };

      return [...currentGroups, newGroup];
    });
  }, []);

  const checkForErrors = useCallback((): boolean => {
    const typesError = appointmentTypes.length === 0;
    const providersError = providers.length === 0;
    const nameError = !ruleName;
    const timeSlotsError =
      groupedTimeRanges.filter((group) => !group.isDeleted).flatMap((group) => group.timeRanges).length === 0;

    setShowTypesError(typesError);
    setShowProvidersError(providersError);
    setShowNameError(nameError);
    setShowTimeSlotsError(timeSlotsError);

    return typesError || providersError || nameError || timeSlotsError || !!timeRangeError;
  }, [appointmentTypes.length, providers.length, ruleName, groupedTimeRanges, timeRangeError]);

  const handleSaveExclusion = useCallback(() => {
    if (checkForErrors()) return;

    const appointmentTypesConnect: AppointmentTypeWhereUniqueInput[] = appointmentTypes.map((id) => {
      return { id };
    });

    const appointmentTypesDisconnect: AppointmentTypeWhereUniqueInput[] =
      selectedExclusion?.appointmentTypes.map(({ id }) => {
        return { id };
      }) || [];

    const providersConnect: ClinicEmployeeWhereUniqueInput[] = providers.map((id) => {
      return { id };
    });

    const providersDisconnect: ClinicEmployeeWhereUniqueInput[] =
      selectedExclusion?.providers.map(({ id }) => {
        return { id };
      }) || [];

    const sharedVariables: DirectBookingExclusionRuleCreateInput | DirectBookingExclusionRuleUpdateInput = {
      clinic: {
        connect: {
          id: clinicId,
        },
      },
      name: ruleName,
      isActive: ruleEnabled,
    };

    // Flatten all time ranges from all groups
    const allTimeRanges = groupedTimeRanges.flatMap((group) => group.timeRanges);

    const timeSlotsCreate: DirectBookingExclusionTimeSlotCreateManyExclusionRuleInput[] = allTimeRanges.map((range) => {
      return {
        dayOfWeek: range.dayOfWeek,
        startTime: range.startAt,
        endTime: range.endAt,
      };
    });

    const timeSlotsUpsert: DirectBookingExclusionTimeSlotUpsertWithWhereUniqueWithoutExclusionRuleInput[] =
      allTimeRanges.map((range) => {
        return {
          where: {
            id: range.isNew ? '' : range.id,
          },
          update: {
            dayOfWeek: range.dayOfWeek,
            startTime: range.startAt,
            endTime: range.endAt,
          },
          create: {
            dayOfWeek: range.dayOfWeek,
            startTime: range.startAt,
            endTime: range.endAt,
          },
        };
      });

    const timeSlotsDisconnect = allTimeRanges
      .filter((range) => !!range.isDeleted)
      .map((range) => {
        return { id: { equals: range.id } };
      });

    try {
      saveExclusion({
        variables: {
          where: {
            id: selectedExclusion?.id || '',
          },
          create: {
            ...(sharedVariables as DirectBookingExclusionRuleCreateInput),
            appointmentTypes: {
              connect: appointmentTypesConnect,
            },
            providers: {
              connect: providersConnect,
            },
            timeSlots: {
              createMany: {
                data: timeSlotsCreate,
                skipDuplicates: true,
              },
            },
          },
          update: {
            ...(sharedVariables as DirectBookingExclusionRuleUpdateInput),
            appointmentTypes: {
              disconnect: appointmentTypesDisconnect,
              connect: appointmentTypesConnect,
            },
            providers: {
              disconnect: providersDisconnect,
              connect: providersConnect,
            },
            timeSlots: {
              upsert: timeSlotsUpsert,
              deleteMany: timeSlotsDisconnect,
            },
          },
        },
      });
    } catch (e) {
      console.error('Error saving exclusion rule', e);
      toast({ title: 'Error saving exclusion rule. Please refresh the page and try again.', status: 'error' });
    }

    onClose();
  }, [
    checkForErrors,
    appointmentTypes,
    selectedExclusion,
    providers,
    clinicId,
    ruleName,
    ruleEnabled,
    groupedTimeRanges,
    onClose,
    saveExclusion,
    toast,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalHeader size="lg">{!selectedExclusion ? 'Add New' : 'Edit'} Exclusion</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {isLoading ? (
          <Center height="510px">
            <LoadingSpinner />
          </Center>
        ) : (
          <Flex direction="column" gap={6}>
            {!!timeRangeError && (
              <Alert
                title="Invalid time slot selection"
                description={timeRangeError}
                status="warning"
                hideCloseButton
              />
            )}
            <Box>
              <TextInput
                label={
                  <LabelPopover
                    title="Exclusionary Rule Name"
                    description="This will only be displayed within your settings page - clients will not be able to see this."
                  />
                }
                labelStyle={{ fontWeight: 'semibold' }}
                placeholder="Please Enter a Name for Your Rule"
                value={ruleName}
                onChange={(event): void => handleNameChange(event.currentTarget.value)}
                isInvalid={showNameError}
                errorText="Please add a name for this exclusionary rule"
                autoFocus
              />
            </Box>
            <Box>
              <Switch
                isFieldInline
                isChecked={ruleEnabled}
                size="md"
                label="Active"
                labelStyle={{ fontWeight: 'semibold' }}
                onChange={(): void => setRuleEnabled((current) => !current)}
              />
            </Box>
            <Box mb={6}>
              <MultiSelectWithTags
                title="Appointment Types"
                items={appointmentTypeItems}
                selectedIds={appointmentTypes}
                onSelectionChange={handleAppointmentTypeSelected}
                showError={showTypesError}
                errorMessage="Please select at least one appointment type"
                emptyMessage="No appointment types selected"
                isLoading={loadingTypes}
              />
            </Box>
            <Box mb={6}>
              <MultiSelectWithTags
                title="Providers"
                items={providerItems}
                selectedIds={providers}
                onSelectionChange={handleProviderSelected}
                showError={showProvidersError}
                errorMessage="Please select at least one provider"
                emptyMessage="No providers selected"
                isLoading={loadingProviders}
              />
            </Box>
            <Flex direction="column">
              <LabelPopover
                title="Exclusionary Time Slots"
                description="Set the recurring days and time windows during which this exclusionary rule will apply."
              />
              <Text mb={1}>Times are in the clinic timezone which is {getTimezoneAbbreviation(clinicTimezone)}.</Text>
              <Flex direction="column" mt={1} gap={2}>
                {groupedTimeRanges
                  .filter((ranges: GroupedTimeRanges) => !ranges.isDeleted)
                  .map((ranges: GroupedTimeRanges) => (
                    <ExclusionaryTimeSlot
                      key={ranges.id}
                      groupedTimeRanges={ranges}
                      startAt={getDateFromTimeString(ranges.startAt, ranges.timeRanges[0]?.updatedAt)}
                      endAt={getDateFromTimeString(ranges.endAt, ranges.timeRanges[0]?.updatedAt)}
                      setGroupedTimeRanges={setGroupedTimeRanges}
                      setErrorMap={setErrorMap}
                      setTimeRangeError={setTimeRangeError}
                    />
                  ))}
              </Flex>
              <Flex py={2} direction="column">
                <Button
                  size="md"
                  width="fit-content"
                  variant="ghost"
                  leftIcon={<Icon size="sm" name="plus" />}
                  onClick={handleAddTimeSlot}
                  border="1px"
                  borderColor={showTimeSlotsError ? 'border.danger' : 'transparent'}
                >
                  Add Time Slot
                </Button>
                {showTimeSlotsError && (
                  <Text size="xs" variant="danger">
                    Please add at least one time slot
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
      </ModalBody>
      <ModalFooterButtons submitText="Save" onSubmit={handleSaveExclusion} />
    </Modal>
  );
};

export default ExclusionsModal;
